import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {add, deleteRecord, fetch, update} from '../services/OffersService';
import {Offer} from '../types';
import {stripReadonlyFields} from '../../../utils/stripReadonlyFields';

export const useOffer = (offerId?: string) => {
    const queryClient = useQueryClient();

    const offerQuery = useQuery({
        queryKey: ['offer', offerId],
        queryFn: () => fetch(offerId!),
        enabled: !!offerId
    });

    const createMutation = useMutation({
        mutationFn: (values: Offer) => {
            return add(stripReadonlyFields(values));
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['offers']});
        }
    });

    const updateMutation = useMutation({
        mutationFn: (values: Offer) => {
            if (!offerId) throw new Error('offerId is required parameter');
            return update(offerId, stripReadonlyFields(values) as Offer);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['offers']});
            queryClient.invalidateQueries({queryKey: ['offer', offerId]});
        }
    });

    const deleteMutation = useMutation({
        mutationFn: () => {
            if (!offerId) throw new Error('offerId is required parameter');
            return deleteRecord(offerId);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['offers']});
        }
    });

    return {
        record: offerQuery.data,
        loading: offerQuery.isLoading,
        fetchRecord: offerQuery.refetch,
        createRecord: createMutation.mutate,
        updateRecord: updateMutation.mutate,
        deleteRecord: deleteMutation.mutate,
        createMutation,
        updateMutation,
        deleteMutation
    };
};
